import { register } from '!/repository'
import { aye, nay } from '@/helpers'
import type { TvChannel, TvShow, Vod } from '@setplex/tria-api'
import { combine, createStore } from 'effector'
import { model as paymentModel } from '~/features/payment/generic'
import {
  getPaymentTvChannelMediaType,
  getPaymentTvShowMediaType,
  getPaymentVodMediaType,
  isFree,
  isPaid,
  type PayableEntity,
} from '~/features/payment/lib'
import { model as guestModel } from '~/features/subscriber/guest'

// TODO: move these helpers to library code
// there are some similar functions located here -> apps/tria/src/features/payment/lib/price-settings.ts

// helper function to check if entity is paid (by internal payments)
const isInternallyPaid =
  (external: boolean) =>
  (entity?: PayableEntity): boolean =>
    isPaid(entity) && nay(external)

// helper function to check if entity is paid (by external payments)
const isExternallyPaid =
  (external: boolean) =>
  (entity?: PayableEntity): boolean =>
    isPaid(entity) && aye(external)

// store-function (pipe) for skin, to check if entity is free
const $isFree = createStore(isFree)

// store-function (pipe) for skin, to check if entity is paid (by internal payments)
export const $isInternallyPaid = combine(
  paymentModel.$externalPaymentEnabled,
  isInternallyPaid
)

// store-function (pipe) for skin, to check if entity is paid (by external payments)
export const $isExternallyPaid = combine(
  paymentModel.$externalPaymentEnabled,
  isExternallyPaid
)

export const $showHowToWatchButton = combine(
  {
    isExternallyPaid: $isExternallyPaid,
    guest: guestModel.$guest,
  },
  ({ isExternallyPaid, guest }) =>
    (entity: PayableEntity) =>
      entity && isExternallyPaid(entity) && !guest
)

export const $showPriceButton = combine(
  {
    isInternallyPaid: $isInternallyPaid,
    guest: guestModel.$guest,
  },
  ({ isInternallyPaid, guest }) =>
    (entity: PayableEntity) =>
      entity && isInternallyPaid(entity) && !guest
)

export const $showSignInToWatchButton = combine(
  {
    isInternallyPaid: $isInternallyPaid,
    isExternallyPaid: $isExternallyPaid,
    guest: guestModel.$guest,
  },
  ({ isInternallyPaid, guest, isExternallyPaid }) =>
    (entity: PayableEntity) =>
      entity && (isExternallyPaid(entity) || isInternallyPaid(entity)) && guest
)

const $tvChannelPaymentMediaType = combine(() => (channel: TvChannel) => {
  if (!channel?.priceSettings) return null

  return getPaymentTvChannelMediaType(channel.priceSettings)
})

const $tvShowPaymentMediaType = combine(() => (show: TvShow) => {
  if (!show?.priceSettings) return null

  return getPaymentTvShowMediaType(show.priceSettings)
})

const $vodPaymentMediaType = combine(() => (vod: Vod) => {
  if (!vod?.priceSettings) return null

  return getPaymentVodMediaType(vod.priceSettings)
})

/*
 * Register payments' pipes
 */

register($isFree, '|payments/isFree')
register($isInternallyPaid, '|payments/isInternallyPaid')
register($isExternallyPaid, '|payments/isExternallyPaid')
register($showHowToWatchButton, '|payments/showHowToWatchButton')
register($showPriceButton, '|payments/showPriceButton')
register($showSignInToWatchButton, '|payments/showSignInToWatchButton')
register($tvChannelPaymentMediaType, '|payments/tvChannelPaymentMediaType')
register($tvShowPaymentMediaType, '|payments/tvShowPaymentMediaType')
register($vodPaymentMediaType, '|payments/vodPaymentMediaType')
